/**
 * Farming patches as this player sees them: the bed, its weeds, and their own crop at its stage.
 * Rebuilt only when the look changes (weeds cleared, a stage grown, watered).
 */
import * as THREE from 'three';
import { CROPS, type GameMap, type MapObject, type PlotView } from '@sunderfen/shared';
import { BUILDERS, G, addMesh, mat } from './models.js';

const mulberry = (a: number) => () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
const PLANK = 0x7a5a38, PLANK_D = 0x5e4228, SOIL = 0x4a3424, SOIL_WET = 0x33241a, LEAF = 0x4f8a34, LEAF_L = 0x74a648;

/** The key that says whether a patch needs redrawing. */
export const plotLook = (v: PlotView, scare = false) => `${scare ? 's' : ''}${v.weeds}|${v.crop ?? ''}|${v.stage}|${v.ready ? 1 : 0}|${v.wetNow ? 1 : 0}|${v.diseased ? 1 : 0}|${v.dead ? 1 : 0}|${v.weedyNow ? 1 : 0}`;

/** The patch at `o`, in world space. */
export function buildPatch(o: MapObject, map: GameMap, v: PlotView, scare = false): THREE.Group {
  const g = new THREE.Group(), w = o.w ?? 1, h = o.h ?? 1, r = mulberry(o.id * 131 + v.stage * 7 + (v.crop?.length ?? 0));
  const cx = o.x + w / 2, cz = o.z + h / 2;
  let y = 0; for (const [dx, dz] of [[0.2, 0.2], [w - 0.2, 0.2], [0.2, h - 0.2], [w - 0.2, h - 0.2], [w / 2, h / 2]]) y += map.heightAt(o.x + dx, o.z + dz);
  y /= 5;
  g.position.set(cx, y, cz);
  const W = w - 0.16, H = h - 0.16, top = 0.2;
  if (scare && v.crop) { const sc = new THREE.Group(); BUILDERS.scarecrow(sc); sc.scale.setScalar(0.62); sc.position.set(W / 2 - 0.12, 0.05, -H / 2 + 0.12); sc.rotation.y = 0.5; g.add(sc); }
  if (o.kind === 'tree' || o.kind === 'fruit') return treeBed(g, o, v, r, W, H);
  if (o.kind === 'bush') return bushBed(g, o, v, r, W, H);
  if (o.kind === 'log') return logBed(g, o, v, r, W, H);
  // the bed: plank sides, pegged at the corners, full of earth
  for (const s of [-1, 1]) {
    addMesh(g, G.sharp(W + 0.1, 0.24, 0.07), s < 0 ? PLANK : PLANK_D, 0, 0.1, s * (H / 2));
    addMesh(g, G.sharp(0.07, 0.24, H), s < 0 ? PLANK_D : PLANK, s * (W / 2), 0.1, 0);
  }
  for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) addMesh(g, G.sharp(0.09, 0.34, 0.09), PLANK_D, x * W / 2, 0.12, z * H / 2);
  const wet = v.wetNow && !!v.crop;
  addMesh(g, G.sharp(W - 0.06, 0.2, H - 0.06), wet ? SOIL_WET : SOIL, 0, 0.06, 0);
  // furrows along the long side
  const along = w >= h, rows = Math.max(2, Math.round((along ? H : W) / 0.42)), len = (along ? W : H) - 0.2;
  const rowAt = (i: number) => -((along ? H : W) - 0.3) / 2 + ((along ? H : W) - 0.3) * (i + 0.5) / rows;
  const put = (a: number, b: number) => (along ? [a, b] : [b, a]) as [number, number];
  for (let i = 0; i < rows; i++) { const [x, z] = put(0, rowAt(i)); const f = addMesh(g, G.cyl(0.07, 0.07, len, 6), wet ? 0x3a2a1e : 0x56402c, x, top - 0.02, z); f.rotation.set(along ? 0 : Math.PI / 2, 0, along ? Math.PI / 2 : 0); f.scale.set(1, 1, 0.6); }

  // weeds: a tangle for each level
  for (let k = 0; k < v.weeds * Math.round(w * h * 2.2); k++) {
    const x = (r() - 0.5) * (W - 0.2), z = (r() - 0.5) * (H - 0.2), s = 0.1 + r() * 0.13;
    addMesh(g, G.ico(s), r() < 0.25 ? 0x8a8a3a : r() < 0.5 ? 0x5a7a2a : 0x6a9a3a, x, top + s * 0.5, z).scale.set(1, 0.7 + r() * 0.8, 1);
    if (r() < 0.3) addMesh(g, G.cyl(0.012, 0.012, 0.35, 4), 0x7a8a3a, x + 0.05, top + 0.18, z).rotation.z = (r() - 0.5) * 0.6;
    if (r() < 0.12) addMesh(g, G.sphere(0.035, 6), r() < 0.5 ? 0xe8d84a : 0xe8e8e0, x, top + s + 0.03, z);
  }

  if (v.crop) {
    const d = CROPS[v.crop], f = v.ready ? 1 : (v.stage + 1) / (d.stages + 1);
    const per = Math.max(2, Math.round(len / 0.42)), crop = new THREE.Group(); g.add(crop);
    for (let i = 0; i < rows; i++) for (let j = 0; j < per; j++) {
      const a = -len / 2 + len * (j + 0.5) / per + (r() - 0.5) * 0.06, [x, z] = put(a, rowAt(i));
      plant(crop, d.look, f, v.ready, x, top, z, r, d.color);
    }
    // blight browns the leaves; dead, it's all straw and slumped
    if (v.diseased || v.dead) {
      const sick = new THREE.Color(v.dead ? 0x6a5a3a : 0x7a6a3a), c = new THREE.Color();
      crop.traverse((m: THREE.Object3D) => {
        const mesh = m as THREE.Mesh; if (!mesh.isMesh) return;
        c.copy((mesh.material as THREE.MeshLambertMaterial).color).lerp(sick, v.dead ? 0.85 : 0.5);
        mesh.material = mat(c.getHex());
      });
      if (v.dead) crop.scale.set(1, 0.55, 1);
      else for (let k = 0; k < 6; k++) addMesh(g, G.sphere(0.04, 6), 0x9a8aa8, (r() - 0.5) * (W - 0.3), top + 0.15 + r() * 0.1, (r() - 0.5) * (H - 0.3)).scale.set(1, 0.4, 1);
    }
    // weeds coming up between the rows
    if (v.weedyNow) for (let k = 0; k < Math.round(w * h * 1.4); k++) addMesh(g, G.ico(0.07 + r() * 0.05), r() < 0.5 ? 0x7a8a2a : 0x5a8a2a, (r() - 0.5) * (W - 0.2), top + 0.05, (r() - 0.5) * (H - 0.2)).scale.set(1, 0.8 + r(), 1);
  }
  g.traverse((c: THREE.Object3D) => { c.userData.obj = o; if ((c as THREE.Mesh).isMesh) { c.castShadow = true; c.receiveShadow = true; } });
  return g;
}

/** A tree patch: a ring of stones round a mound of dug earth, and (in time) your tree. */
function treeBed(g: THREE.Group, o: MapObject, v: PlotView, r: () => number, W: number, H: number) {
  const rad = Math.min(W, H) / 2;
  for (let i = 0; i < 14; i++) { const a = (i / 14) * Math.PI * 2; addMesh(g, G.dod(0.11 + r() * 0.05), r() < 0.5 ? 0x8d8a82 : 0x77736a, Math.cos(a) * rad * 0.92, 0.06, Math.sin(a) * rad * 0.92).rotation.set(r() * 3, r() * 3, 0); }
  addMesh(g, G.sphere(rad * 0.85, 14), v.wetNow ? SOIL_WET : SOIL, 0, -0.05, 0).scale.set(1, 0.22, 1);
  for (let k = 0; k < v.weeds * 5; k++) { const a = r() * 6.28, d = r() * rad * 0.7, s = 0.1 + r() * 0.1; addMesh(g, G.ico(s), r() < 0.5 ? 0x5a7a2a : 0x6a9a3a, Math.cos(a) * d, 0.1 + s * 0.4, Math.sin(a) * d); }
  if (v.crop) {
    const d = CROPS[v.crop], f = v.ready ? 1 : (v.stage + 1) / (d.stages + 1), crop = new THREE.Group(); g.add(crop);
    // a sapling becomes a tree: a trunk that thickens, a crown that fills out
    const ht = 0.3 + f * 2.4, trunk = 0.03 + f * 0.13, leaf = new THREE.Color(d.color ?? '#4e7a34').getHex();
    addMesh(crop, G.cyl(trunk * 0.7, trunk, ht, 7), v.crop === 'silverbark_tree' ? 0xd8dcd8 : 0x6a4a2e, 0, ht / 2 + 0.05, 0);
    const crown = 0.15 + f * 0.95;
    for (let k = 0; k < 4; k++) addMesh(crop, G.ico(crown * (0.7 + r() * 0.3)), k % 2 ? leaf : new THREE.Color(leaf).multiplyScalar(1.15).getHex(), (r() - 0.5) * crown * 0.7, ht + crown * (0.3 + r() * 0.4), (r() - 0.5) * crown * 0.7);
    if (d.fruit && v.ready) for (let k = 0; k < 12; k++) { const a = r() * 6.28, e = r() * 1.2 - 0.2; addMesh(crop, G.sphere(0.06, 8), new THREE.Color(d.fruit).getHex(), Math.cos(a) * crown * 0.85, ht + crown * (0.4 + e * 0.5), Math.sin(a) * crown * 0.85); }
    if (v.diseased || v.dead) { const sick = new THREE.Color(v.dead ? 0x6a5a3a : 0x8a7a3a), c = new THREE.Color(); crop.traverse((m: THREE.Object3D) => { const mesh = m as THREE.Mesh; if (!mesh.isMesh) return; c.copy((mesh.material as THREE.MeshLambertMaterial).color).lerp(sick, v.dead ? 0.8 : 0.45); mesh.material = mat(c.getHex()); }); }
    if (v.weedyNow) for (let k = 0; k < 6; k++) { const a = r() * 6.28, dd = 0.3 + r() * rad * 0.5; addMesh(g, G.ico(0.08), 0x6a8a2a, Math.cos(a) * dd, 0.12, Math.sin(a) * dd); }
  }
  g.traverse((c: THREE.Object3D) => { c.userData.obj = o; if ((c as THREE.Mesh).isMesh) { c.castShadow = true; c.receiveShadow = true; } });
  return g;
}

const finish = (g: THREE.Group, o: MapObject) => { g.traverse((c: THREE.Object3D) => { c.userData.obj = o; if ((c as THREE.Mesh).isMesh) { c.castShadow = true; c.receiveShadow = true; } }); return g; };
const sicken = (crop: THREE.Group, v: PlotView) => {
  if (!v.diseased && !v.dead) return;
  const sick = new THREE.Color(v.dead ? 0x6a5a3a : 0x8a7a3a), c = new THREE.Color();
  crop.traverse((m: THREE.Object3D) => { const mesh = m as THREE.Mesh; if (!mesh.isMesh) return; c.copy((mesh.material as THREE.MeshLambertMaterial).color).lerp(sick, v.dead ? 0.8 : 0.45); mesh.material = mat(c.getHex()); });
  if (v.dead) crop.scale.y *= 0.6;
};

/** A bush patch: a round bed of mulch behind a woven wattle fence, and (in time) your berry bush. */
function bushBed(g: THREE.Group, o: MapObject, v: PlotView, r: () => number, W: number, H: number) {
  const rad = Math.min(W, H) / 2 - 0.05;
  // wattle: stakes round the back half, with withies woven through
  const stakes = 11;
  for (let i = 0; i < stakes; i++) {
    const a = Math.PI * (0.05 + 0.9 * i / (stakes - 1)) + Math.PI;
    addMesh(g, G.cyl(0.025, 0.03, 0.55, 5), 0x6a4e30, Math.cos(a) * rad, 0.27, Math.sin(a) * rad).rotation.z = (r() - 0.5) * 0.1;
  }
  for (const y of [0.16, 0.28, 0.4]) {
    const t = addMesh(g, new THREE.TorusGeometry(rad, 0.022, 4, 18, Math.PI * 0.92), y === 0.28 ? 0x8a6a40 : 0x7a5c38, 0, y, 0);
    t.rotation.set(Math.PI / 2, 0, Math.PI * 0.04);
  }
  // a low rim of stones at the front, and the mulched earth
  for (let i = 0; i < 7; i++) { const a = Math.PI * (0.1 + 0.8 * i / 6); addMesh(g, G.dod(0.08 + r() * 0.04), r() < 0.5 ? 0x8d8a82 : 0x77736a, Math.cos(a) * rad * 0.95, 0.05, Math.sin(a) * rad * 0.95).rotation.set(r() * 3, r() * 3, 0); }
  addMesh(g, G.sphere(rad * 0.88, 14), v.wetNow ? SOIL_WET : 0x5a3e28, 0, -0.04, 0).scale.set(1, 0.2, 1);
  for (let k = 0; k < 14; k++) { const a = r() * 6.28, d = r() * rad * 0.8; addMesh(g, G.sharp(0.1, 0.012, 0.03), r() < 0.5 ? 0x8a6a3a : 0x9a7a48, Math.cos(a) * d, 0.12, Math.sin(a) * d).rotation.y = r() * 3; }
  for (let k = 0; k < v.weeds * 5; k++) { const a = r() * 6.28, d = r() * rad * 0.7, s = 0.09 + r() * 0.1; addMesh(g, G.ico(s), r() < 0.5 ? 0x5a7a2a : 0x6a9a3a, Math.cos(a) * d, 0.1 + s * 0.4, Math.sin(a) * d); }
  if (v.crop) {
    const d = CROPS[v.crop], f = v.ready ? 1 : (v.stage + 1) / (d.stages + 1), crop = new THREE.Group(); g.add(crop);
    const leaf = new THREE.Color(d.color ?? '#4e7a3a'), size = 0.14 + f * 0.48;
    // a few woody canes, then a mound of leafy clumps that fills out round them
    for (let k = 0; k < 4; k++) { const a = k * 1.6 + r(); const c = addMesh(crop, G.cyl(0.012, 0.02, size * 1.4, 4), 0x6a4a2e, Math.cos(a) * 0.05, size * 0.6, Math.sin(a) * 0.05); c.rotation.set(Math.cos(a) * 0.4, 0, Math.sin(a) * 0.4); }
    const clumps = 5 + Math.round(f * 5);
    for (let k = 0; k < clumps; k++) {
      const a = k * 2.4 + r() * 0.5, d2 = size * (0.25 + r() * 0.45), y = size * (0.55 + r() * 0.75);
      const col = leaf.clone().multiplyScalar(0.85 + r() * 0.35).getHex();
      addMesh(crop, G.ico(size * (0.38 + r() * 0.16)), col, Math.cos(a) * d2, y, Math.sin(a) * d2).scale.set(1, 0.8, 1);
    }
    // flowers before the fruit, then clusters of berries on the outside
    if (!v.ready && f > 0.5) for (let k = 0; k < 8; k++) { const a = r() * 6.28, e = 0.4 + r() * 0.8; addMesh(crop, G.sphere(0.022, 5), 0xf4f0e8, Math.cos(a) * size * 0.75, size * (0.5 + e * 0.6), Math.sin(a) * size * 0.75); }
    if (v.ready && d.fruit) {
      const berry = new THREE.Color(d.fruit).getHex();
      for (let k = 0; k < 16; k++) {
        const a = r() * 6.28, e = 0.3 + r() * 0.9, bx = Math.cos(a) * size * 0.78, by = size * (0.45 + e * 0.6), bz = Math.sin(a) * size * 0.78;
        for (let b = 0; b < 3; b++) addMesh(crop, G.sphere(0.03, 6), berry, bx + (r() - 0.5) * 0.05, by - b * 0.03, bz + (r() - 0.5) * 0.05);
      }
    }
    sicken(crop, v);
    if (v.weedyNow) for (let k = 0; k < 6; k++) { const a = r() * 6.28, dd = 0.25 + r() * rad * 0.5; addMesh(g, G.ico(0.07), 0x6a8a2a, Math.cos(a) * dd, 0.1, Math.sin(a) * dd); }
  }
  return finish(g, o);
}

/** A mushroom log: a mossy old log in the shade, and (in time) your mushrooms on it. */
function logBed(g: THREE.Group, o: MapObject, v: PlotView, r: () => number, W: number, H: number) {
  const along = W >= H, len = (along ? W : H) - 0.15;
  // a damp mossy pad, a couple of stones, and the log lying on it
  addMesh(g, G.sphere(0.5, 12), v.wetNow ? 0x2e4a24 : 0x3e6a2e, 0, -0.03, 0).scale.set(along ? len : 0.75, 0.12, along ? 0.75 : len);
  for (const s of [-1, 1]) addMesh(g, G.dod(0.09), 0x77736a, (along ? s * len * 0.42 : 0.3 * s), 0.04, (along ? 0.3 * s : s * len * 0.42)).rotation.set(r() * 3, r() * 3, 0);
  const log = new THREE.Group(); g.add(log); if (!along) log.rotation.y = Math.PI / 2;
  const lr = 0.17;
  addMesh(log, G.cyl(lr, lr * 1.08, len * 0.92, 10), 0x5a4230, 0, lr, 0).rotation.z = Math.PI / 2;
  for (const s of [-1, 1]) addMesh(log, G.cyl(lr * 0.92, lr * 0.92, 0.02, 10), 0xb89a6a, s * len * 0.46, lr, 0).rotation.z = Math.PI / 2;
  // bark ridges and moss on top
  for (let k = 0; k < 8; k++) { const x = (r() - 0.5) * len * 0.85, a = r() * Math.PI; addMesh(log, G.sharp(0.12 + r() * 0.15, 0.025, 0.03), 0x4a3424, x, lr + Math.cos(a) * lr * 0.98, Math.sin(a) * lr * 0.98).rotation.x = a; }
  for (let k = 0; k < 6; k++) addMesh(log, G.sphere(0.06 + r() * 0.04, 6), r() < 0.5 ? 0x5a8a3a : 0x6a9a44, (r() - 0.5) * len * 0.8, lr * 1.9, (r() - 0.5) * 0.12).scale.set(1.6, 0.4, 1);
  for (let k = 0; k < v.weeds * 3; k++) { const s = 0.08 + r() * 0.08; addMesh(g, G.ico(s), r() < 0.5 ? 0x5a7a2a : 0x6a9a3a, (r() - 0.5) * (along ? len : 0.6), s * 0.5, (r() - 0.5) * (along ? 0.6 : len)); }
  if (v.crop) {
    const d = CROPS[v.crop], f = v.ready ? 1 : (v.stage + 1) / (d.stages + 1), crop = new THREE.Group(); log.add(crop);
    const truffle = v.crop === 'truffle_log', cap = new THREE.Color(d.color ?? '#d8c8a8').getHex();
    const n = 4 + Math.round(f * 6);
    for (let k = 0; k < n; k++) {
      const x = -len * 0.4 + len * 0.8 * (k + r() * 0.6) / n, side = r() < 0.5 ? -1 : 1, s = (0.025 + f * 0.05) * (0.7 + r() * 0.6);
      if (truffle) {
        // knobbly dark lumps pushing up through the moss at the foot of the log
        addMesh(crop, G.dod(s * 1.2), cap, x, s * 0.6, side * (lr + 0.08)).rotation.set(r() * 3, r() * 3, 0);
      } else {
        // pale caps on short stems, sprouting from the sides and top of the log
        const a = side * (0.6 + r() * 0.7), y = lr + Math.cos(a) * lr, z = Math.sin(a) * lr;
        const stem = addMesh(crop, G.cyl(s * 0.25, s * 0.3, s * 0.9, 5), 0xf0e8d8, x, y + s * 0.4, z + Math.sign(z) * s * 0.2);
        stem.rotation.x = a * 0.5;
        addMesh(crop, new THREE.SphereGeometry(s, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), cap, x, y + s * 0.85, z + Math.sign(z) * s * 0.4).rotation.x = a * 0.4;
      }
    }
    sicken(crop, v);
  }
  return finish(g, o);
}

function plant(g: THREE.Group, look: string, f: number, ready: boolean, x: number, y: number, z: number, r: () => number, color?: string) {
  const turn = r() * Math.PI * 2;
  switch (look) {
    case 'potato': {
      // a low leafy clump that fills out; when it's ready the haulm yellows and tubers show
      const s = 0.06 + f * 0.13;
      for (let k = 0; k < 3; k++) addMesh(g, G.ico(s * (0.8 + r() * 0.4)), ready ? 0x8a9a3a : k % 2 ? LEAF : LEAF_L, x + Math.cos(turn + k * 2.1) * s * 0.6, y + s * 0.8, z + Math.sin(turn + k * 2.1) * s * 0.6).scale.set(1, 0.75, 1);
      if (f > 0.6 && !ready) addMesh(g, G.sphere(0.022, 6), 0xf0f0e8, x, y + s * 1.7, z);
      if (ready) for (let k = 0; k < 2; k++) addMesh(g, G.sphere(0.055, 8), 0xc8a06a, x + Math.cos(turn + k * 3) * 0.13, y + 0.02, z + Math.sin(turn + k * 3) * 0.13).scale.set(1.25, 0.85, 1);
      break;
    }
    case 'garlic':
    case 'onion': {
      // upright tubular leaves; the bulb swells out of the soil
      const n = 3 + Math.round(f * 2), hgt = 0.08 + f * 0.3;
      for (let k = 0; k < n; k++) { const l = addMesh(g, G.cyl(0.012, 0.016, hgt, 5), ready ? 0x9aaa4a : LEAF_L, x, y + hgt / 2 + 0.02, z); l.rotation.set(Math.cos(turn + k) * 0.25, 0, Math.sin(turn + k) * 0.25); }
      if (f > 0.4) addMesh(g, G.sphere(0.03 + f * 0.05, 8), look === 'garlic' ? 0xf0e8dc : 0xd8b8c8, x, y + 0.02, z).scale.set(1, look === 'garlic' ? 1 : 0.8, 1);
      break;
    }
    case 'cabbage': {
      // a rosette of broad leaves closing into a head
      const s = 0.05 + f * 0.14;
      for (let k = 0; k < 5; k++) { const a = turn + k * 1.26; const l = addMesh(g, G.sphere(s, 8), 0x4a7a5a, x + Math.cos(a) * s * 0.7, y + s * 0.3, z + Math.sin(a) * s * 0.7); l.scale.set(1.1, 0.3, 0.8); l.rotation.y = -a; }
      if (f > 0.35) addMesh(g, G.sphere(s * 0.75, 10), ready ? 0xa8d07a : 0x8ab868, x, y + s * 0.55, z);
      break;
    }
    case 'carrot': {
      // feathery tops; the orange shoulder shows when it's ready
      const n = 4, hgt = 0.08 + f * 0.22;
      for (let k = 0; k < n; k++) { const a = turn + k * 1.6, l = addMesh(g, G.cone(0.05 + f * 0.04, hgt, 5), LEAF_L, x + Math.cos(a) * 0.03, y + hgt / 2 + 0.02, z + Math.sin(a) * 0.03); l.rotation.set(Math.cos(a) * 0.35, 0, Math.sin(a) * 0.35); }
      if (f > 0.5) addMesh(g, G.cyl(0.035, 0.02, 0.06, 8), 0xe0863c, x, y + 0.01, z);
      break;
    }
    case 'pumpkin': {
      // a sprawl of big leaves, and the pumpkin swelling and going orange
      const s = 0.06 + f * 0.1;
      for (let k = 0; k < 3; k++) { const a = turn + k * 2.1, l = addMesh(g, G.sphere(s, 8), 0x3a7a3a, x + Math.cos(a) * s, y + s * 0.3, z + Math.sin(a) * s); l.scale.set(1.2, 0.25, 1); }
      if (f > 0.4) addMesh(g, G.sphere(0.04 + f * 0.1, 12), ready ? 0xe07a2a : 0x8aa84a, x + 0.05, y + 0.05 + f * 0.06, z).scale.set(1.2, 0.85, 1.2);
      break;
    }
    case 'bean': {
      // a cane with a vine twisting up it, pods hanging when it's ready
      const hgt = 0.15 + f * 0.6;
      addMesh(g, G.cyl(0.012, 0.012, hgt + 0.1, 4), 0x9a7a4a, x, y + (hgt + 0.1) / 2, z);
      for (let k = 0; k < 5; k++) addMesh(g, G.sphere(0.04 + f * 0.03, 6), LEAF, x + Math.cos(turn + k * 2.4) * 0.05, y + (k + 0.5) * hgt / 5, z + Math.sin(turn + k * 2.4) * 0.05).scale.set(1.2, 0.5, 0.8);
      if (ready) for (let k = 0; k < 3; k++) addMesh(g, G.capsule(0.012, 0.08), 0x7a9a3a, x + Math.cos(turn + k) * 0.06, y + hgt * (0.4 + k * 0.2), z + Math.sin(turn + k) * 0.06);
      break;
    }
    case 'herb': {
      // a bushy clump of leaves in the herb's own green; flowering when it's ready
      const leaf = new THREE.Color(color ?? '#6a9a4a').getHex(), s = 0.05 + f * 0.11;
      for (let k = 0; k < 6; k++) {
        const a = turn + k * 1.05, l = addMesh(g, G.sphere(s * 0.8, 6), leaf, x + Math.cos(a) * s * 0.7, y + s * (0.6 + (k % 3) * 0.35), z + Math.sin(a) * s * 0.7);
        l.scale.set(1.3, 0.35, 0.6); l.rotation.set(0.5, -a, 0);
      }
      if (ready) for (let k = 0; k < 3; k++) addMesh(g, G.sphere(0.025, 6), [0xf0f0e8, 0xe8c8e8, 0xf0e070][k], x + Math.cos(turn + k * 2) * 0.06, y + s * 2 + 0.04, z + Math.sin(turn + k * 2) * 0.06);
      break;
    }
    case 'flax': {
      // slender stalks with little sky-blue flowers; ready, they go gold with round seed bolls
      const n = 5, hgt = 0.1 + f * 0.6, col = ready ? 0xc8a858 : 0x6a9a4a;
      for (let k = 0; k < n; k++) {
        const ox = Math.cos(turn + k * 1.3) * 0.05, oz = Math.sin(turn + k * 1.3) * 0.05, lean = (r() - 0.5) * 0.2;
        addMesh(g, G.cyl(0.006, 0.008, hgt, 4), col, x + ox, y + hgt / 2, z + oz).rotation.set(lean, 0, (r() - 0.5) * 0.2);
        if (f > 0.5) addMesh(g, G.sphere(ready ? 0.018 : 0.024, 6), ready ? 0xb89a50 : 0x7aa8e8, x + ox + lean * 0.08, y + hgt + 0.01, z + oz).scale.set(1, ready ? 1.1 : 0.4, 1);
      }
      break;
    }
    case 'wheat': {
      // a tuft of stalks that grows tall and goes gold, with ears at the top
      const n = 4, hgt = 0.1 + f * 0.55, col = ready ? 0xd8b850 : f > 0.7 ? 0xa8b850 : 0x6a9a3a;
      for (let k = 0; k < n; k++) {
        const ox = Math.cos(turn + k * 1.6) * 0.05, oz = Math.sin(turn + k * 1.6) * 0.05, lean = (r() - 0.5) * 0.25;
        const s2 = addMesh(g, G.cyl(0.008, 0.01, hgt, 4), col, x + ox, y + hgt / 2, z + oz); s2.rotation.set(lean, 0, (r() - 0.5) * 0.25);
        if (f > 0.55) addMesh(g, G.capsule(0.018, 0.07), ready ? 0xe8c860 : 0xb8c860, x + ox + lean * 0.1, y + hgt + 0.03, z + oz).rotation.z = (r() - 0.5) * 0.4;
      }
      break;
    }
  }
}

/** A crow or a rabbit, come for the crop. Faces +z; `userData.bob` is the part that pecks or hops. */
export function buildPest(kind: string): THREE.Group {
  const g = new THREE.Group(), body = new THREE.Group(); g.add(body); g.userData.bob = body;
  if (kind === 'rabbit') {
    addMesh(body, G.sphere(0.13, 10), 0x8a6a4a, 0, 0.12, 0).scale.set(0.9, 0.85, 1.25);
    addMesh(body, G.sphere(0.08, 10), 0x8a6a4a, 0, 0.2, 0.15);
    for (const s of [-1, 1]) { const e = addMesh(body, G.capsule(0.022, 0.12), 0x9a7a5a, s * 0.03, 0.33, 0.12); e.rotation.z = s * 0.15; e.rotation.x = -0.3; }
    addMesh(body, G.sphere(0.04, 8), 0xe8e0d0, 0, 0.13, -0.17);
    addMesh(body, G.sphere(0.012, 6), 0x1a1a1a, 0.04, 0.22, 0.21);
  } else {
    addMesh(body, G.sphere(0.1, 10), 0x1e1e24, 0, 0.13, 0).scale.set(0.85, 0.85, 1.35);
    addMesh(body, G.sphere(0.065, 10), 0x1e1e24, 0, 0.22, 0.12);
    addMesh(body, G.cone(0.025, 0.09, 5), 0x3a3a3a, 0, 0.21, 0.21).rotation.x = Math.PI / 2;
    for (const s of [-1, 1]) addMesh(body, G.sphere(0.07, 8), 0x2a2a34, s * 0.08, 0.15, -0.02).scale.set(0.35, 0.6, 1.3);
    addMesh(body, G.sharp(0.1, 0.02, 0.12), 0x1e1e24, 0, 0.13, -0.17);
    for (const s of [-1, 1]) addMesh(g, G.cyl(0.008, 0.008, 0.08, 4), 0x5a4a3a, s * 0.04, 0.04, 0);
  }
  return g;
}

/* the quern (a mill station) and the scarecrow */
const STONE = 0x8d8a82;
Object.assign(BUILDERS, {
  mill(g: THREE.Group) {
    // a millstone on a stump under a little lean-to of tin, with a sack beside it
    addMesh(g, G.cyl(0.32, 0.36, 0.45, 10), 0x6a4a30, 0, 0.22, 0);
    addMesh(g, G.cyl(0.36, 0.36, 0.12, 14), STONE, 0, 0.5, 0);
    addMesh(g, G.cyl(0.34, 0.34, 0.1, 14), 0x9a978e, 0, 0.61, 0);
    addMesh(g, G.cyl(0.04, 0.04, 0.3, 6), 0x7a5a38, 0.22, 0.78, 0);
    addMesh(g, G.cyl(0.06, 0.06, 0.04, 8), 0x5a5650, 0, 0.67, 0);
    for (const [x, z] of [[-0.55, -0.45], [0.55, -0.45]]) addMesh(g, G.sharp(0.07, 1.5, 0.07), 0x5e4228, x, 0.75, z);
    for (const [x, z] of [[-0.55, 0.45], [0.55, 0.45]]) addMesh(g, G.sharp(0.07, 1.25, 0.07), 0x5e4228, x, 0.62, z);
    const roof = addMesh(g, G.sharp(1.35, 0.03, 1.15), 0x8a8e90, 0, 1.4, 0); roof.rotation.x = 0.22;
    addMesh(g, G.round(0.26, 0.34, 0.22, 0.08), 0xe0d4b4, -0.45, 0.17, 0.15);
    addMesh(g, G.round(0.22, 0.28, 0.2, 0.08), 0xd8cca8, -0.3, 0.14, 0.38);
  },
  compost_bin(g: THREE.Group) {
    // pallet slats on four posts, heaped with dark compost and a few weeds
    for (const [x, z] of [[-0.42, -0.42], [0.42, -0.42], [-0.42, 0.42], [0.42, 0.42]]) addMesh(g, G.sharp(0.07, 0.8, 0.07), 0x5e4228, x, 0.4, z);
    for (const y of [0.15, 0.35, 0.55]) for (const [w, d, x, z] of [[0.9, 0.04, 0, -0.42], [0.9, 0.04, 0, 0.42], [0.04, 0.9, -0.42, 0], [0.04, 0.9, 0.42, 0]] as const) addMesh(g, G.sharp(w, 0.12, d), y === 0.35 ? 0x8a6a42 : 0x7a5a38, x, y, z);
    addMesh(g, G.sphere(0.4, 10), 0x3e2a1a, 0, 0.5, 0).scale.set(1, 0.45, 1);
    for (let i = 0; i < 5; i++) addMesh(g, G.ico(0.07), i % 2 ? 0x6a8a3a : 0x8a7a3a, Math.cos(i * 1.3) * 0.22, 0.66, Math.sin(i * 1.3) * 0.22);
  },
  tanning_rack(g: THREE.Group) {
    // a pole frame with a hide laced out to dry, a scraping beam and a soaking tub
    for (const x of [-0.5, 0.5]) addMesh(g, G.sharp(0.07, 1.5, 0.07), 0x5e4228, x, 0.75, -0.1);
    addMesh(g, G.sharp(1.15, 0.06, 0.06), 0x5e4228, 0, 1.45, -0.1);
    addMesh(g, G.sharp(1.15, 0.06, 0.06), 0x5e4228, 0, 0.25, -0.1);
    const hide = addMesh(g, G.round(0.8, 1.0, 0.03, 0.12), 0xb88a5a, 0, 0.85, -0.1); hide.rotation.z = 0.05;
    for (const y of [0.4, 0.85, 1.3]) for (const x of [-0.46, 0.46]) addMesh(g, G.box(0.08, 0.015, 0.015), 0xd8c9a0, x, y, -0.1);
    addMesh(g, G.cyl(0.24, 0.2, 0.32, 10), 0x6a4a2a, 0.35, 0.16, 0.4);
    addMesh(g, G.cyl(0.21, 0.21, 0.02, 10), 0x5a4a2a, 0.35, 0.3, 0.4);
    const beam = addMesh(g, G.cyl(0.07, 0.08, 0.9, 7), 0x8a6a42, -0.35, 0.3, 0.38); beam.rotation.x = 1.1;
  },
  spinning_wheel(g: THREE.Group) {
    // a wheel on a slanted bench, with a stool-high spindle and a basket of fleece
    const bench = addMesh(g, G.sharp(0.9, 0.08, 0.3), 0x7a5a38, 0, 0.42, 0); bench.rotation.z = -0.15;
    for (const [x, z] of [[-0.38, -0.12], [-0.38, 0.12], [0.38, 0]]) addMesh(g, G.sharp(0.05, 0.42, 0.05), 0x5e4228, x, 0.21, z);
    for (const s of [-1, 1]) addMesh(g, G.sharp(0.05, 0.5, 0.05), 0x5e4228, -0.1, 0.7, s * 0.09);
    const wheel = addMesh(g, new THREE.TorusGeometry(0.34, 0.025, 6, 24), 0x8a6a42, -0.1, 0.95, 0); wheel.rotation.y = Math.PI / 2;
    for (let i = 0; i < 4; i++) { const sp = addMesh(g, G.box(0.02, 0.66, 0.02), 0x8a6a42, -0.1, 0.95, 0); sp.rotation.x = (i * Math.PI) / 4; }
    addMesh(g, G.cyl(0.03, 0.03, 0.2, 6), 0xefe8d6, 0.35, 0.62, 0).rotation.z = Math.PI / 2;
    addMesh(g, G.cyl(0.2, 0.16, 0.22, 10), 0xb8904a, 0.2, 0.11, 0.45);
    addMesh(g, G.sphere(0.17, 8), 0xf0ead8, 0.2, 0.24, 0.45).scale.set(1, 0.6, 1);
  },
  scarecrow(g: THREE.Group) {
    addMesh(g, G.sharp(0.06, 1.6, 0.06), 0x6a4a2a, 0, 0.8, 0);
    addMesh(g, G.sharp(1.0, 0.05, 0.05), 0x6a4a2a, 0, 1.22, 0);
    addMesh(g, G.round(0.42, 0.55, 0.22, 0.06), 0x5a6a3a, 0, 1.08, 0);
    for (const s of [-1, 1]) addMesh(g, G.round(0.28, 0.14, 0.16, 0.05), 0x5a6a3a, s * 0.33, 1.22, 0);
    addMesh(g, G.sphere(0.15, 10), 0xd8c48a, 0, 1.52, 0);
    addMesh(g, G.cyl(0.13, 0.16, 0.18, 10), 0x7a8288, 0, 1.68, 0);
    for (const s of [-1, 1]) addMesh(g, G.cone(0.04, 0.16, 5), 0xd8c070, s * 0.48, 1.18, 0).rotation.z = s * 2.4;
  },
});
